import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { type ChatCounterpart, DESK_COUNTERPART } from '../../../interface/support-chat';
import { formatFileSize, isDocumentAttachment, MAX_DOCUMENT_BYTES } from '../../../lib/chat-attachment';
import { counterpartCopy } from '../../../lib/chat-counterpart-copy';
import { CameraSVG, PaperclipSVG } from '../../utils/svgs';

interface IChatComposer {
	onSend: (body: string, file: File | null) => void;
	/** Names the other party in the placeholder. */
	counterpart?: ChatCounterpart;
	/** Called on each keystroke. Throttled by the hook, so calling it freely is fine. */
	onTyping?: () => void;
	/** Seeds the field when editing. The caller remounts via key= to reseed. */
	initialBody?: string;
	/** "Send" normally, "Save" while editing — an action keeps one name through its flow. */
	sendLabel?: string;
	disabled?: boolean;
	/** Attachments already queued. Beyond the cap the attach button locks — see below. */
	pendingAttachmentCount: number;
	/**
	 * Text to put in the field, from a quick-message chip. `nonce` makes the same text
	 * insertable twice. Replaces the draft — chips only show while the field is empty — and
	 * focuses the field; it never sends.
	 */
	insert?: { text: string; nonce: number } | null;
	/** Called once the insertion has been applied, so the caller can drop it. */
	onInserted?: () => void;
	/** Reports the field's state, so the caller can show or hide the chips and remember the draft. */
	onDraftChange?: (empty: boolean, text: string) => void;
}

/** One photo or PDF per message, three pending messages carrying one. */
export const MAX_PENDING_ATTACHMENTS = 3;

/**
 * Message composer.
 *
 * <p>Sends optimistically: the caller enqueues to the outbox and the bubble appears
 * immediately. Nothing here awaits the network, because on a field connection awaiting it
 * means a frozen input for up to fifteen seconds.</p>
 */
const ChatComposer = ({
	onSend,
	counterpart = DESK_COUNTERPART,
	onTyping,
	initialBody,
	sendLabel = 'Send',
	disabled,
	pendingAttachmentCount,
	insert,
	onInserted,
	onDraftChange,
}: IChatComposer) => {
	const { composerPlaceholder: placeholder, composerHint } = counterpartCopy(counterpart);
	const [body, setBody] = useState(initialBody ?? '');
	const [file, setFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const docRef = useRef<HTMLInputElement>(null);
	const textRef = useRef<HTMLTextAreaElement>(null);

	// An agent with eight queued photos on 2G will never drain the queue. Capping makes that
	// visible immediately rather than twenty minutes later.
	const attachmentsFull = pendingAttachmentCount >= MAX_PENDING_ATTACHMENTS;
	const canSend = (body.trim().length > 0 || file !== null) && !disabled;

	useEffect(() => {
		onDraftChange?.(body.trim().length === 0 && file === null, body);
	}, [body, file, onDraftChange]);

	useEffect(() => {
		if (!insert) return;
		setBody(insert.text);
		onInserted?.();
		const el = textRef.current;
		if (el) {
			el.focus();
			// Caret at the end, so the next keystroke continues the sentence.
			const end = insert.text.length;
			window.requestAnimationFrame(() => {
				el.setSelectionRange(end, end);
				autoGrow();
			});
		}
		// Keyed on the nonce: the same chip twice is two insertions.
	}, [insert?.nonce]);

	const autoGrow = () => {
		const el = textRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
	};

	const submit = () => {
		if (!canSend) return;
		onSend(body.trim(), file);
		setBody('');
		setFile(null);
		setFileError(null);
		if (fileRef.current) fileRef.current.value = '';
		if (docRef.current) docRef.current.value = '';
		if (textRef.current) textRef.current.style.height = 'auto';
	};

	/**
	 * A PDF over the server's cap is refused HERE, before it spends the agent's data on an upload
	 * the server would reject. The refusal says what to do instead, since the agent cannot shrink
	 * a PDF on a phone — but they can photograph the pages.
	 */
	const pickDocument = (picked: File | null) => {
		if (!picked) return;
		if (!isDocumentAttachment({ contentType: picked.type, fileName: picked.name })) {
			setFileError('Choose a PDF file.');
			return;
		}
		if (picked.size > MAX_DOCUMENT_BYTES) {
			setFileError(`This PDF is ${formatFileSize(picked.size)}. The limit is 20 MB — photograph the pages instead.`);
			if (docRef.current) docRef.current.value = '';
			return;
		}
		setFileError(null);
		setFile(picked);
	};

	const lockedLabel = 'Finish sending your attachments first';

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Enter sends, Shift+Enter newlines. Escape deliberately does NOTHING: discarding a
		// draft on Escape is unrecoverable, and the same key closes the panel, so a stray
		// press would take the message with it.
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			submit();
		}
	};

	return (
		<>
			{fileError ? (
				<p
					className='sc-composer-error'
					role='alert'
				>
					{fileError}
				</p>
			) : null}
			{file ? (
				// The chosen file stays named above the box while the agent types. It used to live
				// only in the placeholder, so the first keystroke hid every sign of an attachment.
				<div
					className='sc-attached'
					role='status'
				>
					<span
						className='sc-attached__badge'
						aria-hidden='true'
					>
						{isDocumentAttachment({ contentType: file.type, fileName: file.name }) ? 'PDF' : 'Photo'}
					</span>
					<span className='sc-attached__name'>{file.name}</span>
					{formatFileSize(file.size) ? <span className='sc-attached__size'>{formatFileSize(file.size)}</span> : null}
					<button
						type='button'
						className='sc-attached__remove'
						aria-label={`Remove ${file.name}`}
						onClick={() => {
							setFile(null);
							if (fileRef.current) fileRef.current.value = '';
							if (docRef.current) docRef.current.value = '';
						}}
					>
						×
					</button>
				</div>
			) : null}
			<div className='support-chat__composer'>
				<input
					ref={fileRef}
					type='file'
					accept='image/*'
					// Camera-first on mobile, matching MultipleMediaUploadComponent's existing bias —
					// an agent photographing a result sheet is the overwhelmingly common case.
					capture='environment'
					hidden
					onChange={(event) => {
						setFileError(null);
						setFile(event.target.files?.[0] ?? null);
					}}
				/>
				{/* A separate picker, not `accept='image/*,application/pdf'` on the one above:
			    `capture` sends a phone straight to the camera, so a combined input could never
			    reach a PDF in Downloads. */}
				<input
					ref={docRef}
					type='file'
					accept='application/pdf,.pdf'
					hidden
					data-testid='sc-document-input'
					onChange={(event) => pickDocument(event.target.files?.[0] ?? null)}
				/>

				<button
					type='button'
					className='sc-attach'
					onClick={() => fileRef.current?.click()}
					disabled={disabled || attachmentsFull}
					aria-label={attachmentsFull ? lockedLabel : 'Attach a photo'}
					title={attachmentsFull ? `${lockedLabel}.` : 'Attach a photo'}
				>
					<CameraSVG />
				</button>
				<button
					type='button'
					className='sc-attach'
					onClick={() => docRef.current?.click()}
					disabled={disabled || attachmentsFull}
					aria-label={attachmentsFull ? lockedLabel : 'Attach a PDF'}
					title={attachmentsFull ? `${lockedLabel}.` : 'Attach a PDF'}
				>
					<PaperclipSVG />
				</button>

				<textarea
					ref={textRef}
					className='sc-input'
					value={body}
					rows={1}
					placeholder={file ? 'Add a message (optional)' : composerHint}
					aria-label={placeholder}
					disabled={disabled}
					onChange={(event) => {
						setBody(event.target.value);
						autoGrow();
						onTyping?.();
					}}
					onKeyDown={handleKeyDown}
				/>

				<button
					type='button'
					className='sc-send'
					onClick={submit}
					disabled={!canSend}
					aria-label={sendLabel}
				>
					➤
				</button>
			</div>
		</>
	);
};

export default ChatComposer;
